// RecurrencePicker.tsx — gekapselte Serientermin-Sektion (SP4). Frequenz +
// (bei wöchentlich) Wochentage + Ende-Modus (Nie / Bis Datum / Nach N). Hält
// das TerminFormWindow schlank. Optik: REFINED-Tokens wie das Termin-Fenster.
//
// Konsistenz B2: Die Datums-Auswahl (Bis-Datum) nutzt RdDatePicker (SP1) —
// KEIN nativer <input type="date"> (bricht .cal-rd-Stil + 24h/dt.-Konsistenz).

import React, { useMemo } from "react";
import moment from "moment";
import "../../utils/momentDe"; // zentrale Locale-Quelle (de, Montag-Wochenstart, SP1)
import type { RecurrenceInput, RecurrenceFrequency, WeekdayName } from "../../services/calendarService";
import RdDatePicker from "./RdDatePicker";
import { REFINED } from "../risikoanalyseStep/tokens";

export type ReminderPreset = "none" | "15" | "30" | "60" | "1440";

export interface RecurrencePickerProps {
    /** null = einmalig. */
    value: RecurrenceInput | null;
    /** Event-Start — Basis für range.startDate + Default-Wochentag. */
    startDate: Date;
    onChange: (rec: RecurrenceInput | null) => void;
    /** read-only Instanz / read-only Event / nicht-absolutes Fremd-Pattern. */
    disabled?: boolean;
}

/** "einmalig" als zusätzliche UI-Option (= value null). */
type FrequencyChoice = "once" | RecurrenceFrequency;

const FREQ_OPTIONS: { value: FrequencyChoice; label: string }[] = [
    { value: "once", label: "Einmalig" },
    { value: "daily", label: "Täglich" },
    { value: "weekly", label: "Wöchentlich" },
    { value: "monthly", label: "Monatlich" },
    { value: "yearly", label: "Jährlich" },
];

const END_OPTIONS: { value: RecurrenceInput["range"]["type"]; label: string }[] = [
    { value: "noEnd", label: "Nie" },
    { value: "endDate", label: "Bis Datum" },
    { value: "numbered", label: "Nach N Vorkommen" },
];

// Mo–So (deutsche Woche beginnt montags) + Graph-Bezeichner.
const WEEKDAYS: { key: WeekdayName; label: string }[] = [
    { key: "monday", label: "Mo" },
    { key: "tuesday", label: "Di" },
    { key: "wednesday", label: "Mi" },
    { key: "thursday", label: "Do" },
    { key: "friday", label: "Fr" },
    { key: "saturday", label: "Sa" },
    { key: "sunday", label: "So" },
];

const JS_DAY_TO_NAME: WeekdayName[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const labelStyle: React.CSSProperties = {
    fontSize: "0.85rem",
    color: REFINED.ink,
    fontWeight: 600,
    marginBottom: 6,
    display: "block",
};

const selectStyle: React.CSSProperties = {
    border: `1.5px solid ${REFINED.line}`,
    borderRadius: 10,
    padding: "10px 12px",
    fontFamily: "inherit",
    fontSize: "0.95rem",
    color: REFINED.ink,
    background: REFINED.white,
    boxSizing: "border-box",
    width: "100%",
};

const numberStyle: React.CSSProperties = {
    ...selectStyle,
    maxWidth: 120,
};

const RecurrencePicker: React.FC<RecurrencePickerProps> = ({ value, startDate, onChange, disabled }) => {
    const startName = useMemo<WeekdayName>(() => JS_DAY_TO_NAME[startDate.getDay()], [startDate]);
    const startYmd = useMemo(() => ymd(startDate), [startDate]);

    const freq: FrequencyChoice = value ? value.pattern.type : "once";

    // Default-Serie beim Wechsel von "einmalig" → eine Frequenz.
    const buildDefault = (f: RecurrenceFrequency): RecurrenceInput => ({
        pattern: {
            type: f,
            interval: 1,
            ...(f === "weekly" ? { daysOfWeek: [startName] } : {}),
        },
        range: { type: "noEnd", startDate: startYmd, recurrenceTimeZone: "Europe/Berlin" },
    });

    const setFreq = (f: FrequencyChoice) => {
        if (f === "once") {
            onChange(null);
            return;
        }
        if (!value) {
            onChange(buildDefault(f));
            return;
        }
        // Frequenz wechseln, Range behalten; daysOfWeek nur bei weekly.
        const pattern: RecurrenceInput["pattern"] = { type: f, interval: value.pattern.interval || 1 };
        if (f === "weekly") {
            pattern.daysOfWeek =
                value.pattern.daysOfWeek && value.pattern.daysOfWeek.length > 0
                    ? value.pattern.daysOfWeek
                    : [startName];
        }
        onChange({ pattern, range: { ...value.range, startDate: value.range.startDate || startYmd } });
    };

    const patchPattern = (p: Partial<RecurrenceInput["pattern"]>) => {
        if (!value) return;
        onChange({ ...value, pattern: { ...value.pattern, ...p } });
    };

    const patchRange = (r: Partial<RecurrenceInput["range"]>) => {
        if (!value) return;
        onChange({ ...value, range: { ...value.range, ...r } });
    };

    const toggleWeekday = (key: WeekdayName) => {
        if (!value) return;
        const current = value.pattern.daysOfWeek ?? [];
        const next = current.includes(key) ? current.filter((d) => d !== key) : [...current, key];
        // Mind. ein Wochentag muss bleiben (Graph fordert das bei weekly).
        patchPattern({ daysOfWeek: next.length > 0 ? next : [startName] });
    };

    const intervalUnit =
        freq === "daily" ? "Tag(e)" : freq === "weekly" ? "Woche(n)" : freq === "monthly" ? "Monat(e)" : "Jahr(e)";

    const endDateValue = value?.range.endDate ? new Date(`${value.range.endDate}T00:00:00`) : startDate;

    return (
        <div style={{ display: "flex", flexDirection: "column", rowGap: 12 }}>
            {/* Frequenz */}
            <div>
                <label style={labelStyle}>Wiederholung</label>
                <select
                    style={selectStyle}
                    value={freq}
                    disabled={disabled}
                    onChange={(e) => setFreq(e.target.value as FrequencyChoice)}
                >
                    {FREQ_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {value && (
                <>
                    {/* Intervall */}
                    <div>
                        <label style={labelStyle}>Alle …</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                                type="number"
                                min={1}
                                max={99}
                                style={numberStyle}
                                value={value.pattern.interval}
                                disabled={disabled}
                                onChange={(e) =>
                                    patchPattern({
                                        interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)),
                                    })
                                }
                            />
                            <span style={{ fontSize: "0.9rem", color: REFINED.ink2 }}>{intervalUnit}</span>
                        </div>
                    </div>

                    {/* Wochentage (nur weekly) — umbruchfähig, Touch-Ziele ≥44px (SP6-Konsistenz) */}
                    {freq === "weekly" && (
                        <div>
                            <label style={labelStyle}>An Wochentagen</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {WEEKDAYS.map((w) => {
                                    const active = (value.pattern.daysOfWeek ?? []).includes(w.key);
                                    return (
                                        <button
                                            key={w.key}
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => toggleWeekday(w.key)}
                                            aria-pressed={active}
                                            style={{
                                                minWidth: 44,
                                                minHeight: 44,
                                                borderRadius: 10,
                                                border: `1.5px solid ${active ? REFINED.aub : REFINED.line}`,
                                                background: active ? REFINED.aub : REFINED.white,
                                                color: active ? REFINED.white : REFINED.ink,
                                                fontFamily: "inherit",
                                                fontSize: "0.85rem",
                                                fontWeight: 600,
                                                cursor: disabled ? "not-allowed" : "pointer",
                                                opacity: disabled ? 0.6 : 1,
                                            }}
                                        >
                                            {w.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Ende-Modus */}
                    <div>
                        <label style={labelStyle}>Endet</label>
                        <select
                            style={selectStyle}
                            value={value.range.type}
                            disabled={disabled}
                            onChange={(e) => {
                                const t = e.target.value as RecurrenceInput["range"]["type"];
                                if (t === "endDate") {
                                    patchRange({
                                        type: t,
                                        endDate: value.range.endDate ?? ymd(startDate),
                                        numberOfOccurrences: undefined,
                                    });
                                } else if (t === "numbered") {
                                    patchRange({
                                        type: t,
                                        numberOfOccurrences: value.range.numberOfOccurrences ?? 10,
                                        endDate: undefined,
                                    });
                                } else {
                                    patchRange({ type: t, endDate: undefined, numberOfOccurrences: undefined });
                                }
                            }}
                        >
                            {END_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {value.range.type === "endDate" && (
                        <div>
                            <label style={labelStyle}>Bis</label>
                            {disabled ? (
                                <div style={{ fontSize: "0.9rem", color: REFINED.ink2 }}>
                                    {moment(endDateValue).format("D. MMMM YYYY")}
                                </div>
                            ) : (
                                <RdDatePicker
                                    value={endDateValue}
                                    onChange={(d) => patchRange({ endDate: ymd(d) })}
                                />
                            )}
                        </div>
                    )}

                    {value.range.type === "numbered" && (
                        <div>
                            <label style={labelStyle}>Anzahl Vorkommen</label>
                            <input
                                type="number"
                                min={1}
                                style={numberStyle}
                                value={value.range.numberOfOccurrences ?? 1}
                                disabled={disabled}
                                onChange={(e) =>
                                    patchRange({
                                        numberOfOccurrences: Math.max(1, Number(e.target.value) || 1),
                                    })
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RecurrencePicker;
